import React, { useEffect } from 'react';
import { Layers, Complementary, Triadic, CircleDot, Sun, Moon, Analogous, Tetradic, SquareHarmony, SplitComplementary, Monochromatic } from '../../components/icons';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { BookOpen } from '../../components/icons';
import AdBanner from '../../components/AdBanner';
import { updateColorTheoryMetadata } from '../../utils/seo';

const ColorTheoryPage: React.FC = () => {
  useEffect(() => { updateColorTheoryMetadata(); }, []);

  return (
    <Layout maxWidth="max-w-5xl">
      <PageHeader icon={BookOpen} title="Color Theory Basics" />
      <div className="ui-panel p-5 sm:p-8">
        
        <div className="ui-prose max-w-none text-ink-secondary">
          <p className="text-base text-ink-secondary mb-8 leading-relaxed">
            Color theory is a practical combination of art and science that's used to determine what colors look good together. The color wheel was invented in 1666 by Isaac Newton, who mapped the color spectrum onto a circle. The color wheel is the basis of color theory, because it shows the relationship between colors.
          </p>

          {/* Section 1: The Color Wheel */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-ink mb-6 flex items-center gap-2">
              <CircleDot className="text-brand-600" size={24} />
              The Color Wheel Structure
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h3 className="font-bold text-ink mb-2">Primary Colors</h3>
                <p className="text-sm">Red, Yellow, Blue. These are the three pigment colors that cannot be mixed or formed by any combination of other colors. All other colors are derived from these three hues.</p>
              </div>
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h3 className="font-bold text-ink mb-2">Secondary Colors</h3>
                <p className="text-sm">Green, Orange, Purple. These are the colors formed by mixing the primary colors together (e.g., Blue + Yellow = Green).</p>
              </div>
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h3 className="font-bold text-ink mb-2">Tertiary Colors</h3>
                <p className="text-sm">Yellow-Orange, Red-Orange, Red-Purple, Blue-Purple, Blue-Green, Yellow-Green. These are formed by mixing a primary and a secondary color.</p>
              </div>
            </div>
          </section>

          {/* Section 2: Color Harmony */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-ink mb-6 flex items-center gap-2">
              <Layers className="text-brand-500" size={24} />
              Color Harmony Rules
            </h2>
            <div className="grid gap-8 md:grid-cols-2">
              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <Complementary className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Complementary</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  Colors that are opposite each other on the color wheel (e.g., Red & Green).
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  The high contrast of complementary colors creates a vibrant look especially when used at full saturation. This color scheme must be managed well so it is not jarring. Best used for buttons or call-to-actions.
                </p>
              </div>

              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <Analogous className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Analogous</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  Colors that are next to each other on the color wheel (e.g., Blue, Blue-Green, Green).
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  Analogous color schemes are often found in nature and are harmonious and pleasing to the eye. Make sure you have enough contrast when choosing an analogous color scheme. Choose one color to dominate, a second to support.
                </p>
              </div>

              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <Triadic className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Triadic</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  Colors that are evenly spaced around the color wheel.
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  Triadic color harmonies tend to be quite vibrant, even if you use pale or unsaturated versions of your hues. To use a triadic harmony successfully, the colors should be carefully balanced - let one color dominate and use the two others for accent.
                </p>
              </div>

              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <SplitComplementary className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Split-Complementary</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  A variation of the complementary color scheme. In addition to the base color, it uses the two colors adjacent to its complement.
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  This color scheme has the same strong visual contrast as the complementary color scheme, but has less tension. It is often a good choice for beginners, because it is difficult to mess up.
                </p>
              </div>

              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <Tetradic className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Tetradic (Rectangle)</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  Uses four colors arranged into two complementary pairs. The hues are evenly spaced around the color wheel in a rectangular pattern.
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  The tetradic scheme is very rich and offers plenty of variety. It works best if you let one color dominate and use the others as accents. Avoid using all four colors in equal amounts, as the composition can become visually overwhelming.
                </p>
              </div>

              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <SquareHarmony className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Square</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  Similar to the tetradic scheme, but with all four colors evenly spaced around the color wheel (90° apart) instead of in a rectangle.
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  The square scheme creates a bold, maximally varied palette. Because every color is equally spaced, no single hue family dominates the wheel — which creates visual tension and energy. Works best in graphic design and illustration rather than UI.
                </p>
              </div>

              <div className="ui-panel p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-3">
                  <Monochromatic className="text-brand-600" size={24} />
                  <h3 className="text-xl font-bold text-ink m-0">Monochromatic</h3>
                </div>
                <p className="text-sm leading-relaxed mb-4">
                  Uses variations in lightness and saturation of a single hue. All colors share the same base hue but differ in tone, tint, and shade.
                </p>
                <p className="text-sm leading-relaxed text-ink-secondary">
                  Monochromatic schemes are visually cohesive and easy to manage. They are ideal for creating a calm, unified look. The challenge is adding enough contrast between tones so elements remain readable and distinct.
                </p>
              </div>
            </div>
          </section>

          <AdBanner slotId="8843219021" format="fluid" className="my-8" />

          {/* Section 3: Color Psychology */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-ink mb-6 flex items-center gap-2">
              <Sun className="text-brand-500" size={24} />
              Color Psychology
            </h2>
            <p className="mb-6">
              Colors evoke emotions and feelings. Understanding these associations is key to creating the right mood for your design.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-orange-50 p-6 rounded-xl border border-orange-100">
                <h3 className="font-bold text-orange-900 mb-3 flex items-center gap-2">
                  <Sun size={18} /> Warm Colors
                </h3>
                <p className="text-sm text-orange-800 mb-2">Red, Orange, Yellow</p>
                <p className="text-xs text-orange-700 leading-relaxed">
                  Warm colors are vivid and energetic, and tend to advance in space. They can convey passion, happiness, enthusiasm, and energy. However, they can also represent danger or anger (especially red).
                </p>
              </div>
              <div className="bg-brand-50 p-6 rounded-xl border border-brand-100">
                <h3 className="font-bold text-brand-900 mb-3 flex items-center gap-2">
                  <Moon size={18} /> Cool Colors
                </h3>
                <p className="text-sm text-brand-800 mb-2">Blue, Green, Purple</p>
                <p className="text-xs text-brand-700 leading-relaxed">
                  Cool colors give an impression of calm, and create a soothing impression. They can convey trust, professionalism, and relaxation. Darker cool colors can also represent sadness or indifference.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Practical Application */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-ink mb-6">How to Apply Color Theory in Practice</h2>
            <p className="mb-6 text-ink-secondary">
              Understanding color harmony rules is only half the battle. Knowing when and how to apply them in real design work is what separates theory from skill.
            </p>
            <div className="space-y-5">
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h4 className="font-bold text-ink mb-2">For Websites and Apps</h4>
                <p className="text-sm text-ink-secondary">Start with an analogous palette for a cohesive background and surface colors, then use a complementary accent for interactive elements like buttons and links. This keeps the interface calm while drawing attention to actionable areas.</p>
              </div>
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h4 className="font-bold text-ink mb-2">For Branding and Logo Design</h4>
                <p className="text-sm text-ink-secondary">Limit your brand palette to 2–3 colors. A primary color defines identity, a secondary color adds variety, and a neutral (white, black, or gray) ensures everything is readable. Use <Link to="/color/0F4C81" className="text-brand-600 hover:underline">blues</Link> for trust, <Link to="/color/D2042D" className="text-brand-600 hover:underline">reds</Link> for energy, and <Link to="/color/228B22" className="text-brand-600 hover:underline">greens</Link> for health or sustainability.</p>
              </div>
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h4 className="font-bold text-ink mb-2">For Print and Marketing Materials</h4>
                <p className="text-sm text-ink-secondary">Triadic and tetradic schemes create visual interest in posters and infographics. Remember that print uses CMYK, not RGB — colors that look vivid on screen may appear duller on paper. Always check the print conversion before finalizing.</p>
              </div>
              <div className="bg-surface p-5 rounded-xl border border-surface-border">
                <h4 className="font-bold text-ink mb-2">For Accessibility</h4>
                <p className="text-sm text-ink-secondary">Color alone should never convey meaning. Always pair color cues with text labels or icons for users with color vision deficiencies. Ensure a contrast ratio of at least 4.5:1 for body text against its background. Use the <Link to="/" className="text-brand-600 hover:underline">Color Match tool</Link> to check your combinations before publishing.</p>
              </div>
            </div>
          </section>

          <AdBanner slotId="8843219021" format="fluid" className="my-8" />

          {/* Section 5: Why Color Theory Matters */}
          <div className="mt-4 pt-8 border-t border-surface-border">
            <h2 className="text-2xl font-bold text-ink mb-6">Why Color Theory Matters in Design</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold shrink-0">1</div>
                <div>
                  <h4 className="font-bold text-ink">Brand Identity</h4>
                  <p className="text-sm text-ink-secondary">Consistent color usage builds brand recognition. Think of Coca-Cola red or Facebook blue. Studies show that color increases brand recognition by up to 80%.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold shrink-0">2</div>
                <div>
                  <h4 className="font-bold text-ink">User Experience (UX)</h4>
                  <p className="text-sm text-ink-secondary">Good contrast ensures readability and accessibility. Color can guide the eye to important elements and establish visual hierarchy. Poor color choices create friction that causes users to leave.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold shrink-0">3</div>
                <div>
                  <h4 className="font-bold text-ink">Conversion Optimization</h4>
                  <p className="text-sm text-ink-secondary">Strategic use of color (like a contrasting button color) can significantly increase click-through rates on Call-to-Action (CTA) buttons. A button that stands out from the page background always outperforms one that blends in.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 font-bold shrink-0">4</div>
                <div>
                  <h4 className="font-bold text-ink">Emotional Communication</h4>
                  <p className="text-sm text-ink-secondary">Colors communicate before words do. A healthcare site that uses aggressive reds feels unsafe. A financial app that uses warm yellows may feel unserious. Aligning your color choices with the emotional intent of your content builds trust at a glance.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-brand-50 border border-brand-100 rounded-xl p-5">
              <p className="text-sm text-brand-900">
                <strong>Ready to apply these principles?</strong> Use the <Link to="/" className="text-brand-700 hover:underline font-medium">Color Match tool</Link> to experiment with color harmonies interactively, or explore our <Link to="/models" className="text-brand-700 hover:underline font-medium">Color Models Guide</Link> to understand how colors are represented in different technical contexts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ColorTheoryPage;
